import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldOff, Loader2 } from "lucide-react";

export default function Security() {
  const { user, refresh } = useAuth();
  const enabled = !!user?.totp_enabled;
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("2要素認証を有効化しました");
      setSetup(null);
      setCode("");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2要素認証を無効化しました");
      setDisableCode("");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="security-page">
      <h1 className="font-display font-bold text-3xl text-stone-900">セキュリティ</h1>
      <p className="mt-2 text-sm text-stone-500">アカウントの2要素認証（2FA）を管理します。</p>

      <div className="mt-8 rounded-lg border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-3">
          {enabled ? (
            <ShieldCheck className="h-6 w-6 text-emerald-700" />
          ) : (
            <ShieldOff className="h-6 w-6 text-stone-400" />
          )}
          <div>
            <div className="font-medium text-stone-900">2要素認証（TOTP）</div>
            <div className="text-sm text-stone-500" data-testid="twofa-status">
              {enabled ? "有効（認証アプリが必要です）" : "無効"}
            </div>
          </div>
        </div>

        {!enabled && !setup && (
          <button
            onClick={startSetup}
            disabled={busy}
            data-testid="twofa-start-button"
            className="mt-6 inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "2要素認証を設定する"}
          </button>
        )}

        {!enabled && setup && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-stone-600">
              認証アプリ（Google Authenticator等）で下のQRコードを読み取るか、シークレットを手入力してください。
            </p>
            <div className="flex flex-col items-center gap-3 rounded-md bg-stone-50 p-4">
              <QRCodeCanvas value={setup.otpauth_uri} size={168} data-testid="twofa-qr" />
              <code className="text-xs text-stone-600 break-all" data-testid="twofa-secret">{setup.secret}</code>
            </div>
            <div>
              <Label htmlFor="enable-code" className="text-stone-700 text-sm">認証コード（6桁）</Label>
              <Input
                id="enable-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                inputMode="numeric"
                data-testid="twofa-enable-code"
                className="mt-1.5 tracking-[0.4em] text-center"
                placeholder="000000"
              />
            </div>
            <button
              onClick={enable}
              disabled={busy || code.length < 6}
              data-testid="twofa-enable-button"
              className="inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "有効化する"}
            </button>
          </div>
        )}

        {enabled && (
          <div className="mt-6 space-y-3">
            <Label htmlFor="disable-code" className="text-stone-700 text-sm">無効化するには認証コードを入力</Label>
            <Input
              id="disable-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              maxLength={6}
              inputMode="numeric"
              data-testid="twofa-disable-code"
              className="max-w-xs tracking-[0.4em] text-center"
              placeholder="000000"
            />
            <button
              onClick={disable}
              disabled={busy || disableCode.length < 6}
              data-testid="twofa-disable-button"
              className="inline-flex items-center justify-center rounded-md border border-orange-300 bg-orange-50 px-4 py-2.5 text-sm font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-60"
            >
              2要素認証を無効化
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
